import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, 
  Mail, 
  MessageSquare, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { logger } from '../../lib/logger';

const NotificationHistory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { notification_type: filter } : {};
      const response = await api.get('/notifications/history', { params });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      logger.error('Failed to load notification history:', error);
      toast.error('Failed to load notification history');
    } finally {
      setLoading(false);
    }
  }, [filter]);


  useEffect(() => {
    fetchHistory();
  }, [filter, fetchHistory]);
  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTemplateLabel = (template) => {
    const labels = {
      'patient_registration': 'Patient Registration',
      'appointment_reminder': 'Appointment Reminder',
      'usage_alerts': 'Usage Alert',
      'subscription_alerts': 'Subscription Alert',
      'payment_confirmation': 'Payment Confirmation',
      'otp_verification': 'OTP Verification'
    };
    return labels[template] || template;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading notification history...</div>
      </div>
    );
  }

  return (
    <div data-testid="notification-history-page">
      <header className="page-header glass-header">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings/notifications')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Notification History</h1>
            <p className="text-slate-500">View all your email and SMS notifications</p>
          </div>
          
          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Notifications</SelectItem>
                <SelectItem value="patient_registration">Patient Registration</SelectItem>
                <SelectItem value="appointment_reminder">Appointment Reminders</SelectItem>
                <SelectItem value="usage_alerts">Usage Alerts</SelectItem>
                <SelectItem value="subscription_alerts">Subscription Alerts</SelectItem>
                <SelectItem value="payment_confirmation">Payment Confirmations</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      <div className="page-content max-w-6xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-[Manrope]">
              Recent Notifications ({notifications.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {notifications.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <p>No notifications found</p>
                <p className="text-sm mt-2">Your notification history will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="p-4 border border-slate-200 rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        notification.channel === 'email' 
                          ? 'bg-blue-100' 
                          : 'bg-green-100'
                      }`}>
                        {notification.channel === 'email' ? (
                          <Mail className="w-5 h-5 text-blue-600" />
                        ) : (
                          <MessageSquare className="w-5 h-5 text-green-600" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-slate-900">
                                {getTemplateLabel(notification.template)}
                              </h3>
                              <Badge variant="outline" className={getStatusColor(notification.status)}>
                                <span className="flex items-center gap-1">
                                  {getStatusIcon(notification.status)}
                                  {notification.status}
                                </span>
                              </Badge>
                            </div>
                            
                            {notification.subject && (
                              <p className="text-sm text-slate-600 mb-1">{notification.subject}</p>
                            )}
                            
                            {notification.message && (
                              <p className="text-sm text-slate-500 italic">{notification.message}</p>
                            )}
                            
                            {notification.error && (
                              <p className="text-sm text-red-600 mt-2">
                                <AlertCircle className="w-3 h-3 inline mr-1" />
                                Error: {notification.error}
                              </p>
                            )}
                          </div>

                          {/* Channel Badge */}
                          <Badge variant="secondary">
                            {notification.channel.toUpperCase()}
                          </Badge>
                        </div>

                        {/* Timestamp */}
                        <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                          <span>Created: {formatDate(notification.created_at)}</span>
                          {notification.sent_at && (
                            <span>Sent: {formatDate(notification.sent_at)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NotificationHistory;
